/**
 * PhoneSales — R7a (2026-05-18) — PII-bearing report.
 *
 * Row-level phone-sale activities. Route gated by RequireRole at
 * router level (admin/superadmin/manager); RPC enforces the same
 * server-side. Mirrors the VerifiedMembers (R5) shape.
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getPhoneSalesReport,
  type PhoneSalesReportRow,
  type SalesActivityReportFilter,
} from "../../services/salesActivities.service";
import {
  resolvePositionNamesBulk,
  indexPositionsById,
  logPiiExport,
} from "../../services/reportsSuite.service";
import ExportButton from "../../components/common/ExportButton";
import ReportFiltersBar, {
  type ReportFilterState,
} from "../../components/reports/ReportFiltersBar";
import ReportPageHeader from "../../components/reports/ReportPageHeader";
import { LockClosedIcon } from "@heroicons/react/24/outline";

const PAGE_SIZE = 50;

const formatDate = (iso?: string | null): string => {
  if (!iso) {
    return "";
  }
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const filterToRpc = (
  f: ReportFilterState,
  page: number,
  limit: number,
): SalesActivityReportFilter => ({
  orgIds: f.orgId ? [f.orgId] : undefined,
  dateFrom: f.dateFrom,
  dateTo: f.dateTo,
  repPositionId: f.repPositionId,
  page,
  limit,
});

const PhoneSales: React.FC = () => {
  const [filters, setFilters] = useState<ReportFilterState>({});
  const [page, setPage] = useState(1);

  React.useEffect(() => {
    setPage(1);
  }, [filters.dateFrom, filters.dateTo, filters.orgId, filters.repPositionId]);

  const { data, isLoading, error } = useQuery({
    queryKey: ["report-phone-sales", filters, page],
    queryFn: () => getPhoneSalesReport(filterToRpc(filters, page, PAGE_SIZE)),
    keepPreviousData: true,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const positionIds = useMemo(
    () =>
      Array.from(
        new Set(
          rows
            .map((r: any) => r.rep_position_id)
            .filter((id: any): id is string => Boolean(id)),
        ),
      ),
    [rows],
  );

  const { data: positionRows } = useQuery({
    queryKey: ["phone-sales-positions", positionIds],
    queryFn: () => resolvePositionNamesBulk(positionIds),
    enabled: positionIds.length > 0,
  });

  const positionsById = useMemo(
    () => indexPositionsById(positionRows ?? []),
    [positionRows],
  );

  const repDisplay = (positionId: string | null) => {
    if (!positionId) {
      return "—";
    }
    const p = positionsById.get(positionId);
    if (!p) {
      return positionId;
    }
    const label = p.assigned_name || p.title;
    return p.is_active ? label : `${label} (inactive)`;
  };

  const EXPORT_COLUMNS = [
    "Activity ID",
    "Created",
    "Rep",
    "Contact First Name",
    "Contact Last Name",
    "Contact Email",
    "Contact Phone",
    "Member Number",
    "Organization ID",
    "Event ID",
    "Status",
    "Notes",
  ];

  const mapRows = async (
    pageRows: PhoneSalesReportRow[],
  ): Promise<Record<string, unknown>[]> => {
    const ids = Array.from(
      new Set(
        pageRows
          .map((r) => r.rep_position_id)
          .filter((id): id is string => Boolean(id)),
      ),
    );
    const positionRowsExport = await resolvePositionNamesBulk(ids);
    const exportPositionsById = indexPositionsById(positionRowsExport);
    const exportRepDisplay = (positionId: string | null) => {
      if (!positionId) {
        return "";
      }
      const p = exportPositionsById.get(positionId);
      if (!p) {
        return positionId;
      }
      const label = p.assigned_name || p.title;
      return p.is_active ? label : `${label} (inactive)`;
    };

    // R5-D5 PII contract: log the export BEFORE returning rows. If the
    // audit log throws, ExportButton's try/catch surfaces a toast and
    // the file does not download. Matches the VerifiedMembers pattern.
    await logPiiExport(
      "phone_sales",
      pageRows.length,
      filters as unknown as Record<string, unknown>,
    );

    return pageRows.map((r) => ({
      "Activity ID": r.activity_id,
      Created: formatDate(r.created_at),
      Rep: exportRepDisplay(r.rep_position_id),
      "Contact First Name": r.contact_first_name ?? "",
      "Contact Last Name": r.contact_last_name ?? "",
      "Contact Email": r.contact_email ?? "",
      "Contact Phone": r.contact_phone ?? "",
      "Member Number": r.member_number ?? "",
      "Organization ID": r.organization_id ?? "",
      "Event ID": r.event_id ?? "",
      Status: r.status,
      Notes: r.notes ?? "",
    }));
  };

  const fetchPage = async (f: ReportFilterState, p: number, l: number) => {
    const res = await getPhoneSalesReport(filterToRpc(f, p, l));
    return { rows: res.rows, total: res.total };
  };

  return (
    <div className="space-y-4">
      <ReportPageHeader
        title="Phone Sales"
        description="Phone sales logged by reps (or by admins on a rep's behalf). Cancelled rows hidden."
        badges={
          <span className="inline-flex items-center gap-1 rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
            <LockClosedIcon className="h-3 w-3" />
            PII
          </span>
        }
        actions={
          <ExportButton
            filters={filters}
            fetchPage={fetchPage}
            mapRows={mapRows}
            columns={EXPORT_COLUMNS}
            filename="phone-sales"
          />
        }
      />

      <ReportFiltersBar
        filters={filters}
        onChange={setFilters}
        showState={false}
        showType={false}
        showRep={true}
      />

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs font-medium uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Rep</th>
              <th className="px-3 py-2">Contact</th>
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Phone</th>
              <th className="px-3 py-2">Member #</th>
              <th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  Loading…
                </td>
              </tr>
            )}
            {error && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-red-600">
                  {(error as Error).message}
                </td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-6 text-center text-gray-500">
                  No phone sales match the current filters.
                </td>
              </tr>
            )}
            {rows.map((r: any) => (
              <tr key={r.activity_id} className="hover:bg-gray-50">
                <td className="px-3 py-2 whitespace-nowrap">
                  {formatDate(r.created_at)}
                </td>
                <td className="px-3 py-2">{repDisplay(r.rep_position_id)}</td>
                <td className="px-3 py-2">
                  {[r.contact_first_name, r.contact_last_name]
                    .filter(Boolean)
                    .join(" ") || "—"}
                </td>
                <td className="px-3 py-2">{r.contact_email || "—"}</td>
                <td className="px-3 py-2">{r.contact_phone || "—"}</td>
                <td className="px-3 py-2">{r.member_number || "—"}</td>
                <td className="px-3 py-2">{r.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-600">
        <div>
          {total > 0
            ? `Page ${page} of ${totalPages} — ${total.toLocaleString()} total`
            : ""}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="rounded border border-gray-300 px-3 py-1 disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default PhoneSales;
